import React, { Suspense } from 'react'
import {
    BrowserRouter as Router, Routes, Route, Navigate,
} from 'react-router-dom';

import { Provider } from 'react-redux'
import { configureStore } from '@reduxjs/toolkit'
import pscStateReducer from './obj/pscState'

import { AuthProvider } from './contexts/AuthContext';

import MainLayout from './components/layouts/Main';
import ProtectedLayout from './components/layouts/Protected';
import PortfolioLayout from './components/layouts/Portfolio';
import RequireRole from './components/layouts/RequireRole';

import RootView from './views/Root'
import ForgotPasswordView from './views/ForgotPassword'
import ResetPasswordView from './views/ResetPassword'
import MainView from './views/Main'
import LoginView from './views/Login'

import CompanySignupView from './views/CompanySignup'
import ResetCompanySignupView from './views/ResetCompanySignup'

import SearchView from './views/Search'
import ChangelogsView from './views/Changelog'

import ProjectPortfolioView from './views/ProjectPortfolio'
import ProjectDashboardView from './views/ProjectDashboard'
import ProjectRedirectToDashboardView from './views/ProjectRedirectToDashboard'
import ProjectView from './views/Project'
import ProjectSharePointView from './views/ProjectSharePoint'
import RedirectToLatestView from './views/RedirectToLatest'
import DocumentsView from './views/Documents'
import DocumentView from './views/Document'
import DocumentSharePointUploadView from './views/DocumentSharePointUpload'
import CustomerDocumentsView from './views/CustomerDocuments'
import CustomerDocumentView from './views/CustomerDocument'
import IncludedDocumentsView from './views/IncludedDocuments'
import IncludedDocumentView from './views/IncludedDocument'
import IncludedNewDocumentView from './views/IncludedNewDocument'
import RecordsView from './views/Records'
import RecordView from './views/Record'
import RecordSharePointUploadView from './views/RecordSharePointUpload'
import DeliveryListsView from './views/DeliveryLists'
import DeliveryListView from './views/DeliveryList'
import DeliveryListDocumentsView from './views/DeliveryListDocuments'
import ReportsView from './views/Reports'
import ReportView from './views/Report'
import ActionItemSourcesView from './views/ActionItemSources'
import ActionItemSourceView from './views/ActionItemSource'
import ActionItemsView from './views/ActionItems'
import ActionItemView from './views/ActionItem'

import SerialNumbersView from './views/SerialNumbers'
import SerialNumberView from './views/SerialNumber'

import QmsDocumentsView from './views/QmsDocuments'
import QmsDocumentView from './views/QmsDocument'
import QmsDocumentSharePointView from './views/QmsDocumentSharePoint'
import QmsDocumentSharePointUploadView from './views/QmsDocumentSharePointUpload'
import QaDocumentsView from './views/QaDocuments'
import QaDocumentView from './views/QaDocument'
import QaDocumentSharePointView from './views/QaDocumentSharePoint'
import QaDocumentSharePointUploadView from './views/QaDocumentSharePointUpload'
import QaRecordsView from './views/QaRecords'
import QaRecordView from './views/QaRecord'
import QaRecordSharePointView from './views/QaRecordSharePoint'
import QaRecordSharePointUploadView from './views/QaRecordSharePointUpload'

import UsersView from './views/Users'
import UserView from './views/User'
import UsergroupsView from './views/Usergroups'
import UsergroupView from './views/Usergroup'
import SecurityView from './views/Security'

// Account
import AccountUpdatePasswordView from './views/Account/UpdatePassword';
import Account2FAView from './views/Account/2FA';

import RedirectToMainView from './views/RedirectToMain';

// Integrations
import SharePointCallback from './views/SharePointCallback'
import SharePointOpenDocumentView from './views/SharePointOpenDocument'

// Cadm
import CadmView from './views/Cadm'
import CadmObjectsView from './views/Cadm/CadmObjects'
import CadmObjectView from './views/Cadm/CadmObject'
import ClassificationsView from './views/Cadm/Classifications'
import ModelsView from './views/Cadm/Models'
import ModelView from './views/Cadm/Model'
import QmsDocumentTypesView from './views/Cadm/QmsDocumentTypes'
import QmsDocumentTypeView from './views/Cadm/QmsDocumentType'
import QaDocumentTypesView from './views/Cadm/QaDocumentTypes'
import QaDocumentTypeView from './views/Cadm/QaDocumentType'
import QaRecordTypesView from './views/Cadm/QaRecordTypes'
import QaRecordTypeView from './views/Cadm/QaRecordType'
import ReportTypesView from './views/Cadm/ReportTypes'
import ReportTypeView from './views/Cadm/ReportType'
import ReportSchemesView from './views/Cadm/ReportSchemes'
import StdDocumentsView from './views/Cadm/StdDocuments'
import StdDocumentView from './views/Cadm/StdDocument'
import ReferenceStructuresView from './views/Cadm/ReferenceStructures'
import ReferenceStructureView from './views/Cadm/ReferenceStructure'
import TemplatePlaceholdersView from './views/Cadm/TemplatePlaceholders'
import TemplatesView from './views/Cadm/Templates'
import TemplateView from './views/Cadm/Template'
import ProjectConfigurationSchemesView from './views/Cadm/ProjectConfigurationSchemes'
import ProjectConfigurationSchemeView from './views/Cadm/ProjectConfigurationScheme'
import TypecodeGroupsView from './views/Cadm/TypecodeGroups'
import TypecodeGroupView from './views/Cadm/TypecodeGroup'
import TypecodeGroupMappingsView from './views/Cadm/TypecodeGroupMappings'
import TypecodeGroupMappingView from './views/Cadm/TypecodeGroupMapping'
import ReportSchemeView from './views/Cadm/ReportScheme'
import OAuthClientsView from './views/Cadm/OAuthClients'
import OAuthClientView from './views/Cadm/OAuthClient'
import SharePointApplicationView from './views/Cadm/SharePointApplication'

import 'bootstrap/dist/css/bootstrap.min.css'
import 'font-awesome/css/font-awesome.css'
//import './styles/awesome-bootstrap-checkbox.css'
//import './styles/react-datetime.css'
import 'react-nestable/dist/styles/index.css';
import 'react-datepicker/dist/react-datepicker.css'
import 'datatables.net-dt/css/dataTables.dataTables.min.css'
import 'filepond/dist/filepond.min.css'

import './styles/style.css'
import './styles/gncgo.css'

const store = configureStore({
    reducer: {
        pscState: pscStateReducer
    }
})

const App = () => {
    return (
        <Provider store={store}>
            <AuthProvider>
                <Router>
                    <Suspense fallback={<div className="loading">Loading...</div>}>
                        <Routes>
                            <Route path="/" element={<RootView />} />
                            <Route element={<MainLayout />}>
                                <Route path="/forgot_password" element={<ForgotPasswordView />} />
                                <Route path="/reset_password" element={<ResetPasswordView />} />
                                <Route path="/login" element={<LoginView />} />
                                <Route path="/company_signup" element={<CompanySignupView />} />
                                <Route path="/reset_company_signup" element={<ResetCompanySignupView />} />
                                <Route element={<ProtectedLayout />}>{/* Ensures user is logged in */}
                                    <Route path="/main" element={<MainView />} />
                                    <Route path="/search" element={<SearchView />} />
                                    <Route path="/changelog" element={<ChangelogsView />} />
                                    <Route path="/account" element={<RedirectToMainView />} />
                                    <Route path="/account/update_password" element={<AccountUpdatePasswordView />} />
                                    <Route path="/account/2fa" element={<Account2FAView />} />
                                    <Route path="/sharepoint_callback" element={<SharePointCallback />} />
                                    <Route path="/sharepoint/open" element={<SharePointOpenDocumentView />} />
                                    <Route path="/portfolio" element={<ProjectPortfolioView />} />
                                    <Route element={<PortfolioLayout />}>{/* Ensures state.project is defined. NOTE still need to check in useEffect code :-/ */}
                                        <Route path="/portfolio/:projectID" element={<ProjectRedirectToDashboardView />} />
                                        <Route path="/portfolio/:projectID/dashboard" element={<ProjectDashboardView />} />
                                        <Route path="/portfolio/:projectID/information" element={<ProjectView />} />
                                        <Route path="/portfolio/:projectID/integrations/sharepoint" element={<ProjectSharePointView />} />
                                        <Route path="/portfolio/:projectID/documents" element={<DocumentsView />} />
                                        <Route path="/portfolio/:projectID/documents/:objID" element={<DocumentView />} />
                                        <Route path="/portfolio/:projectID/documents/:objID/latest" element={<RedirectToLatestView type="documents" />} />
                                        <Route path="/portfolio/:projectID/documents/:objID/sharepoint_upload" element={<DocumentSharePointUploadView />} />
                                        <Route path="/portfolio/:projectID/customer_documents" element={<CustomerDocumentsView />} />
                                        <Route path="/portfolio/:projectID/customer_documents/:objID" element={<CustomerDocumentView />} />
                                        <Route path="/portfolio/:projectID/customer_documents/:objID/latest" element={<RedirectToLatestView type="customer_documents" />} />
                                        <Route path="/portfolio/:projectID/included_documents" element={<IncludedDocumentsView />} />
                                        <Route path="/portfolio/:projectID/included_documents/new" element={<IncludedNewDocumentView />} />
                                        <Route path="/portfolio/:projectID/included_documents/:objID" element={<IncludedDocumentView />} />
                                        <Route path="/portfolio/:projectID/records" element={<RecordsView />} />
                                        <Route path="/portfolio/:projectID/records/:objID" element={<RecordView />} />
                                        <Route path="/portfolio/:projectID/records/:objID/sharepoint_upload" element={<RecordSharePointUploadView />} />
                                        <Route path="/portfolio/:projectID/delivery_lists" element={<DeliveryListsView />} />
                                        <Route path="/portfolio/:projectID/delivery_lists/:objID" element={<DeliveryListView />} />
                                        <Route path="/portfolio/:projectID/delivery_lists/:objID/documents" element={<DeliveryListDocumentsView />} />
                                        <Route path="/portfolio/:projectID/reports" element={<ReportsView />} />
                                        <Route path="/portfolio/:projectID/reports/:objID" element={<ReportView />} />
                                        <Route path="/portfolio/:projectID/action_item_sources" element={<ActionItemSourcesView />} />
                                        <Route path="/portfolio/:projectID/action_item_sources/:objID" element={<ActionItemSourceView />} />
                                        <Route path="/portfolio/:projectID/action_items" element={<ActionItemsView />} />
                                        <Route path="/portfolio/:projectID/action_items/:objID" element={<ActionItemView />} />
                                    </Route>
                                    
                                    <Route element={<RequireRole role="serial_numbers_readonly" />}>
                                        <Route path="/serial_numbers" element={<SerialNumbersView/>} />
                                        <Route path="/serial_numbers/:objID" element={<SerialNumberView/>} />
                                    </Route>

                                    <Route element={<RequireRole role="qms_documents_readonly" />}>
                                        <Route path="/qms_documents" element={<QmsDocumentsView/>} />
                                        <Route path="/qms_documents/:objID" element={<QmsDocumentView/>} />
                                        <Route path="/qms_documents/:objID/latest" element={<RedirectToLatestView type="qms_documents" />} />
                                        <Route path="/qms_documents/:objID/sharepoint_upload" element={<QmsDocumentSharePointUploadView/>} />
                                    </Route>

                                    <Route element={<RequireRole role="qms_documents_admin" />}>
                                        <Route path="/qms_documents/admin/sharepoint" element={<QmsDocumentSharePointView/>} />
                                    </Route>

                                    <Route element={<RequireRole role="qa_documents_readonly" />}>
                                        <Route path="/qa_documents" element={<QaDocumentsView/>} />
                                        <Route path="/qa_documents/:objID" element={<QaDocumentView/>} />
                                        <Route path="/qa_documents/:objID/latest" element={<RedirectToLatestView type="qa_documents" />} />
                                        <Route path="/qa_documents/:objID/sharepoint_upload" element={<QaDocumentSharePointUploadView/>} />
                                    </Route>

                                    <Route element={<RequireRole role="qa_documents_admin" />}>
                                        <Route path="/qa_documents/admin/sharepoint" element={<QaDocumentSharePointView/>} />
                                    </Route>

                                    <Route element={<RequireRole role="qa_records_readonly" />}>
                                        <Route path="/qa_records" element={<QaRecordsView/>} />
                                        <Route path="/qa_records/:objID" element={<QaRecordView/>} />
                                        <Route path="/qa_records/:objID/sharepoint_upload" element={<QaRecordSharePointUploadView/>} />
                                    </Route>

                                    <Route element={<RequireRole role="qa_records_admin" />}>
                                        <Route path="/qa_records/admin/sharepoint" element={<QaRecordSharePointView/>} />
                                    </Route>

                                    <Route element={<RequireRole role="user_admin"/>}>
                                        <Route path="/users" element={<UsersView/>} />
                                        <Route path="/users/:userID" element={<UserView/>} />
                                        <Route path="/security" element={<SecurityView/>} />
                                        <Route path="/usergroups" element={<UsergroupsView/>} />
                                        <Route path="/usergroups/:objID" element={<UsergroupView/>} />
                                    </Route>
                                
                                    <Route element={<RequireRole role="cadm_admin" />}>
                                        <Route path="/cadm" element={<CadmView/>} />
                                        <Route path="/cadm/models" element={<ModelsView/>} />
                                        <Route path="/cadm/models/:objID" element={<ModelView/>} />
                                        <Route path="/cadm/cadm_objects" element={<CadmObjectsView/>} />
                                        <Route path="/cadm/cadm_objects/:objID" element={<CadmObjectView/>} />
                                        <Route path="/cadm/classifications" element={<ClassificationsView/>} />
                                        <Route path="/cadm/report_types" element={<ReportTypesView/>} />
                                        <Route path="/cadm/report_types/:objID" element={<ReportTypeView/>} />
                                        <Route path="/cadm/report_schemes/:objID" element={<ReportSchemeView/>} />
                                        <Route path="/cadm/report_schemes" element={<ReportSchemesView/>} />
                                        <Route path="/cadm/template_placeholders" element={<TemplatePlaceholdersView/>} />
                                        <Route path="/cadm/templates" element={<TemplatesView/>} />
                                        <Route path="/cadm/templates/:objID" element={<TemplateView/>} />
                                        <Route path="/cadm/std_documents" element={<StdDocumentsView/>} />
                                        <Route path="/cadm/std_documents/:objID" element={<StdDocumentView/>} />
                                        <Route path="/cadm/qms_document_types" element={<QmsDocumentTypesView/>} />
                                        <Route path="/cadm/qms_document_types/:objID" element={<QmsDocumentTypeView/>} />
                                        <Route path="/cadm/qa_document_types" element={<QaDocumentTypesView/>} />
                                        <Route path="/cadm/qa_document_types/:objID" element={<QaDocumentTypeView/>} />
                                        <Route path="/cadm/qa_record_types" element={<QaRecordTypesView/>} />
                                        <Route path="/cadm/qa_record_types/:objID" element={<QaRecordTypeView/>} />
                                        <Route path="/cadm/reference_structures" element={<ReferenceStructuresView/>} />
                                        <Route path="/cadm/reference_structures/:objID" element={<ReferenceStructureView/>} />
                                        <Route path="/cadm/project_configuration_schemes" element={<ProjectConfigurationSchemesView/>} />
                                        <Route path="/cadm/project_configuration_schemes/:objID" element={<ProjectConfigurationSchemeView/>} />
                                        <Route path="/cadm/typecode_groups" element={<TypecodeGroupsView/>} />
                                        <Route path="/cadm/typecode_groups/:objID" element={<TypecodeGroupView/>} />
                                        <Route path="/cadm/typecode_group_mappings" element={<TypecodeGroupMappingsView/>} />
                                        <Route path="/cadm/typecode_group_mappings/:objID" element={<TypecodeGroupMappingView/>} />

                                        <Route path="/cadm/oauth_clients" element={<OAuthClientsView/>} />
                                        <Route path="/cadm/oauth_clients/:objID" element={<OAuthClientView/>} />

                                        <Route path="/cadm/sharepoint_application" element={<SharePointApplicationView/>} />
                                    </Route>
                                </Route>
                            </Route>
                            <Route path="*" element={<Navigate to="/main" replace />} />
                        </Routes>
                    </Suspense>
                </Router>
            </AuthProvider>
        </Provider>
    );
}
export default App
